import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import {
  ShoppingCart, DeliveryDining, ContactMail,
  People, Store, LocalOffer
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import { PageHeader, MetricCard, useFeedback } from '../components/ui';
import { color, radius } from '../theme/tokens';

const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: color.ink[500],
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
);

const Dashboard = () => {
  const feedback = useFeedback();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/admin/dashboard-stats')
      .then(res => setStats(res.data))
      .catch(() => { setStats({}); feedback.error('Failed to load dashboard stats'); })
      .finally(() => setLoading(false));
  }, [feedback]);

  const groups = [
    {
      label: 'Orders & enquiries',
      metrics: [
        { to: '/orders',        label: 'Total Orders',           value: stats?.totalOrders,        icon: <ShoppingCart /> },
        { to: '/fast-delivery', label: 'Fast Delivery Requests', value: stats?.fastDeliveryOrders, icon: <DeliveryDining />, hint: 'Express fulfilment' },
        { to: '/enquiries',     label: 'Contact Enquiries',      value: stats?.contactEnquiries,   icon: <ContactMail />,    hint: 'Support inbox' },
      ],
    },
    {
      label: 'Audience & network',
      metrics: [
        { to: '/users',   label: 'Registered Users', value: stats?.totalUsers,      icon: <People /> },
        { to: '/stores',  label: 'Active Stores',    value: stats?.activeStores,    icon: <Store />,      hint: 'Live branches' },
        { to: '/coupons', label: 'Active Discounts', value: stats?.activeDiscounts, icon: <LocalOffer />, hint: 'Running offers' },
      ],
    },
  ];

  return (
    <Box>
      <PageHeader
        eyebrow="Overview"
        title="Dashboard"
        subtitle="A live snapshot of orders, enquiries and the store network across the HotSpot console."
      />

      {groups.map((group, gi) => (
        <Box key={group.label} sx={{ mb: gi < groups.length - 1 ? 3 : 0 }}>
          <SectionLabel>{group.label}</SectionLabel>
          <Grid container spacing={2}>
            {group.metrics.map((m) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={m.label}>
                <Box
                  component={Link}
                  to={m.to}
                  sx={{
                    display: 'block',
                    height: '100%',
                    color: 'inherit',
                    textDecoration: 'none',
                    borderRadius: `${radius.xl}px`,
                    '&:focus-visible': {
                      outline: `2px solid ${color.primary[500]}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  <MetricCard
                    label={m.label}
                    value={m.value}
                    hint={m.hint}
                    icon={m.icon}
                    loading={loading}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};

export default Dashboard;
